import { Vector2Tuple } from 'three';

export type SchemaRuleCondition = SchemaRuleChangeCondition;
export type SchemaRuleAction = SchemaRuleSetAction;

export interface SchemaRule {
  conditions: SchemaRuleCondition[];
  actions: SchemaRuleAction[];
}

export enum SchemaFieldTypesEnum {
  Boolean = 'boolean',
  String = 'string',
  Number = 'number',
  Enum = 'enum',
  Vector2 = 'vec2',
  Vector3 = 'vec3',
  Vector4 = 'vec4',
  Matrix3 = 'mat3',
  Matrix4 = 'mat4',
  Polygon = 'polygon',
  Furniture = 'furniture',
}

export type SchemaFieldTypesMapping = {
  boolean: boolean;
  string: string;
  number: number;
  enum: string | number;
  vec2: Vector2Tuple;
  polygon: Vector2Tuple[][];
  furniture: number;
};

export type SchemaFieldNumber = {
  type: 'number';
  label?: string;
  description?: string;
  min?: number | string;
  max?: number | string;
  step?: number | string;
  default?: number | string;
};

export type SchemaFieldEnum<TType extends string | number> = {
  type: 'enum';
  label?: string;
  description?: string;
  options: (
    | {
        label: string;
        value: TType;
      }
    | TType
  )[];
  default?: TType;
};

export type SchemaFieldFurniture = {
  type: 'furniture';
  label?: string;
  description?: string;
  options: {
    label: string;
    value: number;
  }[];
  default?: number;
  furnitureTypes?: string[];
};

export type SchemaFieldBoolean = {
  type: 'boolean';
  label?: string;
  description?: string;
  default?: boolean;
};

export type SchemaFieldString = {
  type: 'string';
  label?: string;
  description?: string;
  default?: string;
};

export type SchemaFieldVector2 = {
  type: 'vec2';
  label?: string;
  description?: string;
  min?: number | Vector2Tuple | string;
  max?: number | Vector2Tuple | string;
  step?: number | Vector2Tuple | string;
  default?: number | Vector2Tuple | string;
};

export type SchemaFieldPolygon = {
  type: 'polygon';
  label?: string;
  description?: string;
  default?: string;
};

export type SchemaField =
  | SchemaFieldBoolean
  | SchemaFieldNumber
  | SchemaFieldString
  | SchemaFieldEnum<number>
  | SchemaFieldEnum<string>
  | SchemaFieldVector2
  | SchemaFieldPolygon;

export type SchemaFields = {
  [key: string]: SchemaField;
};

export type SchemaEvaluatedField =
  | SchemaFieldBoolean
  | SchemaFieldString
  | {
      type: 'number';
      label?: string;
      description?: string;
      min?: number;
      max?: number;
      step?: number;
      default?: number;
    }
  | SchemaFieldEnum<number>
  | SchemaFieldEnum<string>
  | {
      type: 'vec2';
      label?: string;
      description?: string;
      min?: Vector2Tuple;
      max?: Vector2Tuple;
      step?: Vector2Tuple;
      default?: Vector2Tuple;
    }
  | SchemaFieldPolygon;

export interface Schema {
  version: string;
  name: string;
  fields: SchemaFields;
  rules?: SchemaRule[];
  order?: string[];
}

export type SchemaValues<TSchemaFields extends SchemaFields> = {
  [K in keyof TSchemaFields]: TSchemaFields[K]['type'] extends 'enum'
    ? Exclude<TSchemaFields[K]['default'], undefined>
    : SchemaFieldTypesMapping[TSchemaFields[K]['type']];
};

export type SchemaEvaluatedFieldValues<TSchemaField extends SchemaField> = Extract<
  SchemaEvaluatedField,
  { type: TSchemaField['type'] }
>;

export type SchemaMergeArray<TSchemas extends Schema[]> = {
  version: string;
  name: string;
  fields: Intersection<
    {
      [K in keyof TSchemas]: TSchemas[K]['fields'];
    }[number]
  >;
};

export type SchemaMerge<TSchemaA extends Schema, TSchemaB extends Schema> = {
  version: string;
  name: string;
  fields: Union<TSchemaA['fields'] & TSchemaB['fields']>;
};

export interface SchemaRuleChangeCondition {
  type: 'change';
  field: string;
}

export interface SchemaRuleSetAction {
  type: 'set';
  field: string;
  value: string;
}

export interface SchemaVariables {
  [key: string]: any;
}
